import { useMemo, useState } from "react";
import { faFilter } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Box,
  Checkbox,
  Divider,
  Popover,
  PopoverProps,
  ScrollArea,
  Stack,
  TextInput,
} from "@mantine/core";

export interface CompactMultiSelectProps {
  options: string[];
  selectedItems: string[];
  onChange: (items: string[]) => void;
  isItemLocked?: (item: string) => boolean;
  searchable?: boolean;
  selectAll?: boolean;
  moveSelectedToTop?: boolean;
  popoverProps?: Omit<PopoverProps, "children">;
}

/**
 *
 * A compact multi-select component that renders a button with a dropdown menu with checkboxes.
 * You can pass in button props to change the look of the button.
 * @param options - the options to select from. These must be unique.
 * @param selectedItems - the items that are currently selected
 * @param onChange - a callback that is called when the selection changes
 * @param isItemLocked - a callback that returns true if the item is locked and cannot be selected/deselected
 * @param searchable - whether a search box is shown to filter the options
 * @param selectAll - whether a select all checkbox is shown
 * @param moveSelectedToTop - whether the selected items are moved to the top of the list
 * @param popoverProps - props to pass to the popover ex. position="bottom-end"
 */
export const CompactMultiSelect: React.FC<CompactMultiSelectProps> = ({
  options,
  selectedItems,
  onChange,
  isItemLocked,
  searchable,
  selectAll,
  moveSelectedToTop,
  popoverProps,
  children,
}) => {
  const [filterText, setFilterText] = useState<string>();

  const filteredOptions = useMemo(() => {
    if (!filterText) {
      return options;
    }
    return options.filter((option) =>
      option.toLowerCase().includes(filterText.toLowerCase())
    );
  }, [filterText, options]);

  const itemsMovedToTop = moveSelectedToTop ? selectedItems : [];

  // we need to manually set the width of the popover if the width prop is passed in
  // the width could be a string, a number, or "target"
  // we need the box width to be 34px less than the popover width to account for the scrollbar and padding
  let boxWidth: string | undefined = undefined;
  if (popoverProps?.width) {
    if (typeof popoverProps.width === "number") {
      boxWidth = `calc(${popoverProps.width}px - 34px)`;
    } else if (popoverProps.width !== "target") {
      boxWidth = `calc(${popoverProps.width} - 34px)`;
    }
  }

  // select all options that are not explicitly locked
  const onSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const selectableOptions = options.filter(
        (option) => !isItemLocked || !isItemLocked(option)
      );
      onChange(selectableOptions);
    } else {
      onChange([]);
    }
  };

  return (
    <Popover {...popoverProps}>
      <Popover.Target>{children}</Popover.Target>
      {/* no right padding because we want the scrollbar to be all the way to the right */}
      <Popover.Dropdown p="xs" pr={0}>
        <Stack spacing="xs">
          {searchable && (
            <TextInput
              placeholder="Filter..."
              icon={<FontAwesomeIcon icon={faFilter} />}
              value={filterText}
              onChange={(event) => setFilterText(event.currentTarget.value)}
              mr="xs"
            />
          )}
          <ScrollArea.Autosize p={0} maxHeight={200} offsetScrollbars>
            <Box sx={boxWidth ? { width: boxWidth } : undefined}>
              {selectAll && (
                <>
                  <Checkbox
                    label="Select all"
                    checked={selectedItems.length === options.length}
                    indeterminate={
                      selectedItems.length > 0 &&
                      selectedItems.length < options.length
                    }
                    onChange={onSelectAllChange}
                  />
                  <Divider my="xs" />
                </>
              )}
              <Checkbox.Group
                spacing="xs"
                orientation="vertical"
                value={selectedItems}
                onChange={onChange}
                offset={0}
              >
                {filteredOptions
                  .filter((item) => itemsMovedToTop.includes(item))
                  .map((item) => (
                    <Checkbox
                      key={item.replace(/ /g, "-")}
                      data-testid={item.replace(/ /g, "-")}
                      label={item}
                      value={item}
                      disabled={isItemLocked && isItemLocked(item)}
                      styles={() => ({
                        label: { overflow: "hidden", textOverflow: "ellipsis" },
                      })}
                    />
                  ))}
                {filteredOptions
                  .filter((item) => !itemsMovedToTop.includes(item))
                  .map((item) => (
                    <Checkbox
                      key={item.replace(/ /g, "-")}
                      data-testid={item.replace(/ /g, "-")}
                      label={item}
                      value={item}
                      disabled={isItemLocked && isItemLocked(item)}
                      styles={() => ({
                        label: { overflow: "hidden", textOverflow: "ellipsis" },
                      })}
                    />
                  ))}
              </Checkbox.Group>
            </Box>
          </ScrollArea.Autosize>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};
